import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'Speed Reading with Audio - Listen at 3x Speed';
const heroDescription =
  'Accelerate your reading with AI-powered audio. Listen to documents at up to 3x speed while maintaining perfect comprehension and clarity.';

const featureTitle = heroTitle;
const featureSubtitle = 'Read faster than ever before';
const featureDescription = heroDescription;
const featureBadge = 'Productivity Boost';
const featureVariant = 'default';
const featureCtaText = 'Try Speed Reading';

const featureFeatures = [
  {
    title: 'Variable Speed Control',
    description:
      'Adjust playback speed from 0.75x to 3x in precise 10% increments for optimal comprehension.',
  },
  {
    title: 'Clarity at High Speeds',
    description:
      'AI voices maintain natural pronunciation and clarity even at maximum speed settings.',
  },
  {
    title: 'Adaptive Learning',
    description:
      'Start slow and gradually increase speed as you become comfortable with faster audio.',
  },
];

const comparisonTableData = {
  title: 'Audio vs Visual Speed Reading: What Works Best?',
  description:
    'Compare audio-based speed reading with traditional visual speed reading apps and conventional reading methods.',
  columns: [
    {
      header: 'Audio Speed Reading',
      subheader: 'Listening',
      isHighlighted: true,
    },
    { header: 'Visual Speed Reading Apps', subheader: 'Spreeder, SwiftRead' },
    { header: 'Traditional', subheader: 'Reading' },
  ],
  rows: [
    {
      feature: 'Reading Speed',
      description:
        'How fast you can process information while maintaining understanding.',
      values: [
        '3x speed with full comprehension',
        '1000+ WPM but poor comprehension',
        '200-300 WPM',
      ],
    },
    {
      feature: 'Comprehension',
      description: 'Level of understanding and retention of the material read.',
      values: [
        '95%+ maintained at all speeds',
        'Drops significantly above 600 WPM',
        'High but slow',
      ],
    },
    {
      feature: 'Eye Strain',
      description:
        'Physical discomfort and fatigue experienced during reading.',
      values: ['None', 'High - causes headaches', 'Low'],
    },
    {
      feature: 'Multitasking',
      description:
        'Ability to perform other activities while consuming content.',
      values: [
        'Listen while walking, driving, exercising',
        'Requires screen focus',
        'Requires text focus',
      ],
    },
    {
      feature: 'Academic Content',
      description:
        'Effectiveness when reading technical and academic documents.',
      values: [
        'Excellent for research papers',
        'Poor for complex material',
        'Good but slow',
      ],
    },
    {
      feature: 'Scientific Terms',
      description:
        'Handling of complex technical terminology and pronunciation.',
      values: [
        'Perfect pronunciation',
        'No pronunciation help',
        'Good but slow',
      ],
    },
    {
      feature: 'Note-Taking',
      description: 'Ability to take notes while consuming content.',
      values: [
        'Hands-free while listening',
        'Cannot write while reading',
        'Traditional notes',
      ],
    },
    {
      feature: 'Accessibility',
      description:
        'Support for users with reading difficulties or disabilities.',
      values: [
        'Great for dyslexia/visual impairments',
        'Limited support',
        'Limited support',
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'Revolutionary Audio-Based Speed Reading That Actually Works',
    content:
      'Traditional speed reading apps like Spreeder and SwiftRead rely on visual techniques that sacrifice comprehension for speed, but audio-based speed reading offers a breakthrough solution that maintains both clarity and understanding. While conventional speed reading tools display text rapidly on screen (RSVP method), forcing your eyes to track at uncomfortable speeds, our AI-powered audio technology allows you to absorb information naturally at up to 3x speed without eye strain or mental fatigue',
    contentSecondary:
      'Research shows that listening comprehension often exceeds reading comprehension, especially at accelerated speeds. Unlike visual speed reading apps that require intense focus and can cause headaches, audio speed reading lets you multitask, take notes, and process complex academic or professional content while commuting, exercising, or working. With precise speed control from 0.75x to 3x in 10% increments, you can find your optimal learning pace and gradually increase speed as your audio processing improves.',
  },
  {
    title: 'Why Audio Speed Reading Beats Spreeder, SwiftRead, and Visual Apps',
    content:
      "While popular speed reading apps like Spreeder focus on eliminating subvocalization and reducing eye movements, they ignore a fundamental problem: comprehension dramatically drops at high visual reading speeds. Studies show that most people plateau around 400-600 words per minute with visual speed reading before understanding suffers. Audio speed reading eliminates this barrier entirely by leveraging your brain's natural ability to process spoken language at accelerated speeds.",
    contentSecondary:
      'The key difference lies in cognitive load: visual speed reading apps force your brain to decode text, track eye movements, and process meaning simultaneously, creating mental bottlenecks. Audio speed reading removes the decoding burden, allowing full cognitive capacity for comprehension and retention. Users consistently report better understanding of complex materials compared to traditional speed reading tools, with testimonials noting "significantly faster, easier, and clearer to absorb textbooks" and "perfect for academic papers where comprehension matters most."',
  },
];

export const Route = createFileRoute('/_marketing/_seo/speed-reading')({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title: 'Speed Reading Audio Tool - Listen at 3x Speed | Listening',
        description:
          'Speed reading with audio technology. Listen to documents at 3x speed with perfect comprehension. Better than traditional speed reading apps and tools',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
    />
  ),
});
